import { withMountedPathSvgAsync } from "./svgUtils";
import {
    collectFillPackTargetsWithOptions,
    getBBoxGridSpec,
    isPointInsideGeometryFill,
} from "./fillPackGeometry";
import {
    startFrameBudget,
    yieldIfFrameExhausted,
} from "./cooperativeScheduler";
import {
    fingerprintSvgContent,
    hashPlacementToUnit,
} from "./pathPopulatePlacement";

function throwIfAborted(signal: AbortSignal): void {
    if (signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
    }
}

export interface FillPackPlacement {
    x: number;
    y: number;
    angle: number;
    regionIndex: number;
    objectIndex: number;
    /**
     * Integer ≥ 1; instance scale is base scale × this (randomized between min/max).
     * Packing reserves an s×s block of grid cells (anchor at min corner i,j).
     */
    cellSpan: number;
}

export type FillPackPlacementMode = "alternating" | "random";
export type FillPackRotationMode = "align" | "random" | "none";

export type FillPackProgressPhase = "mount" | "scan" | "filter";

export interface FillPackProgress {
    phase: FillPackProgressPhase;
    processed: number;
    total: number;
}

export interface FillPackRegion {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

export interface FillPackPlacementComputeInput {
    pathSvgContent: string;
    spacing: number;
    inset: number;
    objectCount: number;
    objectPlacementMode: FillPackPlacementMode;
    rotationMode: FillPackRotationMode;
    baseRotationDeg: number;
    cellSpanMin: number;
    cellSpanMax: number;
    explicitFillOnly: boolean;
    packRegion: FillPackRegion | null;
    signal: AbortSignal;
    onProgress?: (info: FillPackProgress) => void;
}

export function countGridCellsForBBox(
    bbox: DOMRect,
    spacing: number,
    inset: number,
): number {
    const spec = getBBoxGridSpec(bbox, spacing, inset);
    return spec ? spec.nx * spec.ny : 0;
}

function gridHashOrdinal(gridI: number, gridJ: number): number {
    return (gridJ & 0xffff) | ((gridI & 0xffff) << 16);
}

/** Largest s ≤ maxCap such that every cell center in the s×s block from (i,j) lies in fill. */
function maxSpanAllCentersInsideFill(
    root: SVGSVGElement,
    el: SVGGeometryElement,
    startX: number,
    startY: number,
    spacing: number,
    i: number,
    j: number,
    nx: number,
    ny: number,
    maxCap: number,
): number {
    const maxByDim = Math.min(nx - i, ny - j);
    const maxS = Math.min(maxCap, maxByDim);
    for (let s = maxS; s >= 1; s--) {
        let ok = true;
        for (let dj = 0; dj < s && ok; dj++) {
            for (let di = 0; di < s && ok; di++) {
                const x = startX + (i + di) * spacing;
                const y = startY + (j + dj) * spacing;
                if (!isPointInsideGeometryFill(root, el, x, y)) {
                    ok = false;
                }
            }
        }
        if (ok) return s;
    }
    return 0;
}

function blockUnoccupied(
    occupied: Uint8Array,
    nx: number,
    i: number,
    j: number,
    s: number,
): boolean {
    for (let dj = 0; dj < s; dj++) {
        for (let di = 0; di < s; di++) {
            if (occupied[(j + dj) * nx + (i + di)]) return false;
        }
    }
    return true;
}

function markBlockOccupied(
    occupied: Uint8Array,
    nx: number,
    i: number,
    j: number,
    s: number,
): void {
    for (let dj = 0; dj < s; dj++) {
        for (let di = 0; di < s; di++) {
            occupied[(j + dj) * nx + (i + di)] = 1;
        }
    }
}

function pickPreferredSpan(
    regionIndex: number,
    gridI: number,
    gridJ: number,
    minSpan: number,
    maxSpan: number,
): number {
    let mn = Math.max(1, Math.floor(minSpan));
    let mx = Math.max(1, Math.floor(maxSpan));
    if (mx < mn) {
        const t = mn;
        mn = mx;
        mx = t;
    }
    const range = mx - mn + 1;
    const u = hashPlacementToUnit(
        regionIndex,
        gridHashOrdinal(gridI, gridJ),
        0x63656c53,
    );
    return mn + Math.min(range - 1, Math.floor(u * range));
}

function assignObjectIndicesAndAngles(
    raw: { x: number; y: number; regionIndex: number; cellSpan: number }[],
    objectCount: number,
    objectPlacementMode: FillPackPlacementMode,
    rotationMode: FillPackRotationMode,
    baseRotationDeg: number,
): FillPackPlacement[] {
    if (objectCount <= 0) return [];

    return raw.map((p, idx) => {
        let objectIndex: number;
        if (objectPlacementMode === "alternating") {
            objectIndex = idx % objectCount;
        } else {
            // Grid-aligned (x, y) makes LCG seeds from coordinates look striped;
            // hashPlacementToUnit matches other tools and spreads across [0, 1).
            const uObj = hashPlacementToUnit(p.regionIndex, idx, 0x4f626a49);
            objectIndex = Math.min(
                objectCount - 1,
                Math.floor(uObj * objectCount),
            );
        }

        let angleDeg = baseRotationDeg;
        if (rotationMode === "random") {
            const uRot = hashPlacementToUnit(p.regionIndex, idx, 0x726f7452);
            angleDeg = uRot * 360 + baseRotationDeg;
        } else if (rotationMode === "none") {
            angleDeg = baseRotationDeg;
        } else {
            // align: no path tangent in fill pack — keep upright
            angleDeg = baseRotationDeg;
        }

        return {
            x: p.x,
            y: p.y,
            angle: (angleDeg * Math.PI) / 180,
            regionIndex: p.regionIndex,
            objectIndex,
            cellSpan: p.cellSpan,
        };
    });
}

/**
 * Fingerprint for dependency keys (path SVG + numeric params).
 */
export function fillPackPlacementDepsKey(input: {
    pathSvgContent: string;
    spacing: number;
    inset: number;
    objectCount: number;
    objectPlacementMode: FillPackPlacementMode;
    rotationMode: FillPackRotationMode;
    baseRotationDeg: number;
    cellSpanMin: number;
    cellSpanMax: number;
    explicitFillOnly: boolean;
    packRegion: FillPackRegion | null;
}): string {
    const r = input.packRegion;
    const regionKey = r
        ? `${r.minX}:${r.minY}:${r.maxX}:${r.maxY}`
        : "none";
    return [
        fingerprintSvgContent(input.pathSvgContent),
        input.spacing,
        input.inset,
        input.objectCount,
        input.objectPlacementMode,
        input.rotationMode,
        input.baseRotationDeg,
        input.cellSpanMin,
        input.cellSpanMax,
        input.explicitFillOnly ? 1 : 0,
        regionKey,
    ].join("|");
}

export async function computeFillPackPlacementsAsync(
    input: FillPackPlacementComputeInput,
): Promise<{ regionCount: number; placements: FillPackPlacement[] }> {
    const {
        pathSvgContent,
        spacing,
        inset,
        objectCount,
        objectPlacementMode,
        rotationMode,
        baseRotationDeg,
        cellSpanMin,
        cellSpanMax,
        explicitFillOnly,
        packRegion,
        signal,
        onProgress,
    } = input;

    if (spacing <= 0 || objectCount <= 0) {
        return { regionCount: 0, placements: [] };
    }

    const regionIsValid =
        packRegion != null &&
        Number.isFinite(packRegion.minX) &&
        Number.isFinite(packRegion.minY) &&
        Number.isFinite(packRegion.maxX) &&
        Number.isFinite(packRegion.maxY) &&
        packRegion.maxX > packRegion.minX &&
        packRegion.maxY > packRegion.minY;
    const blockInsideRegion = (
        i: number,
        j: number,
        s: number,
        startX: number,
        startY: number,
    ): boolean => {
        if (!regionIsValid || !packRegion) return true;
        const blockMinX = startX + (i - 0.5) * spacing;
        const blockMinY = startY + (j - 0.5) * spacing;
        const blockMaxX = startX + (i + s - 0.5) * spacing;
        const blockMaxY = startY + (j + s - 0.5) * spacing;
        return (
            blockMinX >= packRegion.minX &&
            blockMinY >= packRegion.minY &&
            blockMaxX <= packRegion.maxX &&
            blockMaxY <= packRegion.maxY
        );
    };

    const budget = startFrameBudget();
    onProgress?.({ phase: "mount", processed: 0, total: 1 });

    const result = await withMountedPathSvgAsync(
        pathSvgContent,
        async (root) => {
            throwIfAborted(signal);
            const targets = collectFillPackTargetsWithOptions(root, {
                explicitFillOnly,
            });
            onProgress?.({ phase: "scan", processed: targets.length, total: 1 });

            let totalCells = 0;
            const bboxes: DOMRect[] = [];
            for (const t of targets) {
                throwIfAborted(signal);
                await yieldIfFrameExhausted(budget, signal);
                const bbox = t.el.getBBox();
                bboxes.push(bbox);
                totalCells += countGridCellsForBBox(bbox, spacing, inset);
            }

            const candidates: {
                x: number;
                y: number;
                regionIndex: number;
                cellSpan: number;
            }[] = [];
            let processedCells = 0;

            let mnSpan = Math.max(1, Math.floor(cellSpanMin));
            let mxSpan = Math.max(1, Math.floor(cellSpanMax));
            if (mxSpan < mnSpan) {
                const t = mnSpan;
                mnSpan = mxSpan;
                mxSpan = t;
            }

            for (let ti = 0; ti < targets.length; ti++) {
                throwIfAborted(signal);
                const { el, regionIndex } = targets[ti];
                const bbox = bboxes[ti];
                const spec = getBBoxGridSpec(bbox, spacing, inset);
                if (!spec) continue;

                const { startX, startY, nx, ny } = spec;
                const occupied = new Uint8Array(nx * ny);

                for (let j = 0; j < ny; j++) {
                    for (let i = 0; i < nx; i++) {
                        await yieldIfFrameExhausted(budget, signal);
                        processedCells++;
                        if (processedCells % 64 === 0 || processedCells === totalCells) {
                            onProgress?.({
                                phase: "filter",
                                processed: processedCells,
                                total: Math.max(1, totalCells),
                            });
                        }

                        if (occupied[j * nx + i]) continue;

                        const maxGeom = maxSpanAllCentersInsideFill(
                            root,
                            el,
                            startX,
                            startY,
                            spacing,
                            i,
                            j,
                            nx,
                            ny,
                            mxSpan,
                        );
                        if (maxGeom < mnSpan) continue;

                        const cappedMax = Math.min(mxSpan, maxGeom);
                        if (cappedMax < mnSpan) continue;

                        const preferred = pickPreferredSpan(
                            regionIndex,
                            i,
                            j,
                            mnSpan,
                            cappedMax,
                        );

                        let placedSpan = 0;
                        for (let tryS = preferred; tryS >= mnSpan; tryS--) {
                            if (tryS > maxGeom) continue;
                            if (!blockUnoccupied(occupied, nx, i, j, tryS)) {
                                continue;
                            }
                            if (
                                !blockInsideRegion(i, j, tryS, startX, startY)
                            ) {
                                continue;
                            }
                            placedSpan = tryS;
                            break;
                        }

                        if (placedSpan <= 0) continue;

                        markBlockOccupied(occupied, nx, i, j, placedSpan);
                        const cx =
                            startX + (i + (placedSpan - 1) / 2) * spacing;
                        const cy =
                            startY + (j + (placedSpan - 1) / 2) * spacing;
                        candidates.push({
                            x: cx,
                            y: cy,
                            regionIndex,
                            cellSpan: placedSpan,
                        });
                    }
                }
            }

            const placements = assignObjectIndicesAndAngles(
                candidates,
                objectCount,
                objectPlacementMode,
                rotationMode,
                baseRotationDeg,
            );

            return { regionCount: targets.length, placements };
        },
    );

    return result ?? { regionCount: 0, placements: [] };
}
